import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../services/auth';
import { X } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useToast } from '../../hooks/useToast';
import { AuthError, getErrorMessage, ErrorCodes } from '../../lib/errors';
import { GoogleSignIn } from './GoogleSignIn';
import { EULAModal } from './EULAModal';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const [acceptedEULA, setAcceptedEULA] = useState(false);
  const [isEULAModalOpen, setIsEULAModalOpen] = useState(false);
  const [isConfirmationSent, setIsConfirmationSent] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const { signIn, signUp, resendConfirmationEmail } = useAuthStore();
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setShowResendOption(false);
      setAcceptedEULA(false);
      setLoading(false);
      setIsConfirmationSent(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setShowResendOption(false);
    setIsConfirmationSent(false);

    try {
      if (!isLogin) {
        if (!acceptedEULA) {
          toast.error('Please accept the End User License Agreement to create an account');
          setLoading(false);
          return;
        }
        if (password !== confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
      }

      if (isLogin) {
        await signIn(email, password);
        toast.success('Successfully signed in!');
        onClose();
      } else {
        const { data } = await signUp(email, password);
        
        if (data?.user?.identities?.length === 0) {
          toast.error('An account with this email already exists');
          setLoading(false);
          return;
        }
        
        setConfirmationEmail(email);
        setIsConfirmationSent(true);
        toast.success('Account created! Please check your email to confirm your account.');
      }
    } catch (error) {
      console.error('Auth error:', error);
      const message = getErrorMessage(error as AuthError);
      toast.error(message);

      if ((error as AuthError).code === ErrorCodes.UserNotConfirmed) {
        setShowResendOption(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    try {
      await resendConfirmationEmail(email);
      toast.success('Confirmation email resent!');
    } catch (error) {
      console.error('Error resending confirmation:', error);
      toast.error('Failed to resend confirmation email');
    }
  };

  return (
    <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-auto bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-xl border border-white/10">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6">
          {/* Header */}
          <h2 className="text-2xl font-semibold text-white mb-6">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>

          {/* Google Sign In */}
          <div className="mb-6 space-y-4">
            <GoogleSignIn onSignInSuccess={onClose} />
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-800/95 text-white/60">
                  Or continue with email
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 bg-white/5 text-white border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-transparent transition-colors"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 bg-white/5 text-white border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-transparent transition-colors"
                required
              />
            </div>

            {!isLogin && (
              <>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-1">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 text-white border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-transparent transition-colors"
                    required
                  />
                </div>
                <div className="flex items-start space-x-2">
                  <input
                    id="eula"
                    type="checkbox"
                    checked={acceptedEULA}
                    onChange={(e) => setAcceptedEULA(e.target.checked)}
                    className="mt-1.5 shrink-0 w-3.5 h-3.5 rounded border-white/10 text-gold-400 focus:ring-gold-400/50"
                    required
                  />
                  <label htmlFor="eula" className="text-sm text-white/80">
                    I accept the <button 
                      type="button"
                      onClick={() => setIsEULAModalOpen(true)}
                      className="text-gold-400 hover:text-gold-300 underline transition-colors"
                    >
                      End User License Agreement
                    </button>
                  </label>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading || (!isLogin && !acceptedEULA)}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-gold-400 hover:bg-gold-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-400 disabled:opacity-50 disabled:cursor-not-allowed mt-6 transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <LoadingSpinner className="w-5 h-5 mr-3" />
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                <>{isLogin ? 'Sign In' : 'Create Account'}</>
              )}
            </button>
          </form>

          {/* Toggle login/register */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setShowResendOption(false);
                setAcceptedEULA(false);
              }}
              className="text-sm text-white/60 hover:text-white transition-colors"
            >
              {isLogin ? "Don't have an account? Create one" : 'Already have an account? Sign in'}
            </button>
          </div>

          {/* Resend confirmation option */}
          {showResendOption && (
            <div className="mt-4 text-center">
              <button
                onClick={handleResendConfirmation}
                className="text-sm text-gold-400 hover:text-gold-300 transition-colors"
              >
                Resend confirmation email
              </button>
            </div>
          )}
        </div>
      </div>
      {isConfirmationSent ? (
        <div className="p-8">
          <h2 className="text-2xl font-semibold text-white mb-4">Check Your Email</h2>
          <p className="text-white/80 mb-4">
            We've sent a confirmation email to <span className="text-gold-400">{confirmationEmail}</span>.
            Please check your inbox and click the confirmation link to activate your account.
          </p>
          <p className="text-white/60 text-sm mb-6">
            If you don't see the email, please check your spam folder.
          </p>
          {showResendOption && (
            <button
              onClick={handleResendConfirmation}
              disabled={loading}
              className="text-gold-400 hover:text-gold-300 text-sm underline disabled:opacity-50 transition-colors"
            >
              Resend confirmation email
            </button>
          )}
        </div>
      ) : (
        <div className="p-8">
          <EULAModal 
            isOpen={isEULAModalOpen} 
            onClose={() => setIsEULAModalOpen(false)} 
          />
        </div>
      )}
    </div>
  );
};
